import React, { useEffect, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { api } from "../../lib/apiClient";
import { toast } from "sonner";
import { Loader2, CalendarClock, Clock, MapPin, User, CheckCircle2, XCircle, MessageSquare, MessageCircle, Pencil, Phone, Trash2, ChevronDown, ChevronRight, Search, FileText } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const statusColor = { requested: "#eab308", approved: "#3b82f6", confirmed: "#22c55e", completed: "#71717a", cancelled: "#ef4444" };
const DURATIONS = [
  { v: 30, l: "30 min" }, { v: 60, l: "1 hour" }, { v: 120, l: "2 hours" }, { v: 240, l: "Half day (4h)" },
  { v: 480, l: "Full day" }, { v: 960, l: "2 days" }, { v: 1440, l: "3 days" }, { v: 1920, l: "4 days" },
  { v: 2400, l: "1 week" }, { v: 4800, l: "2 weeks" },
];
const gbp = (pence) => `£${((pence || 0) / 100).toFixed(2)}`;

export default function Bookings() {
  const { company } = useOutletContext();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState(null);
  const [approving, setApproving] = useState(null); // job being approved
  const [duration, setDuration] = useState("60");
  const [slot, setSlot] = useState("");
  const [busy, setBusy] = useState(false);
  const [notify, setNotify] = useState(null); // job to tell the customer about
  const [showCompleted, setShowCompleted] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [query, setQuery] = useState("");
  const [completing, setCompleting] = useState(null); // job being marked completed
  const [priceIn, setPriceIn] = useState("");
  const [matIn, setMatIn] = useState("");

  const openComplete = (job) => { setPriceIn(job.job_price ?? ""); setMatIn(job.material_cost ?? ""); setCompleting(job); };

  const confirmComplete = async () => {
    await api.patch(`/jobs/${completing.id}`, {
      status: "completed",
      job_price: priceIn === "" ? null : Number(priceIn),
      material_cost: matIn === "" ? null : Number(matIn),
    });
    toast.success("Job completed");
    setCompleting(null);
    load();
  };

  const [editing, setEditing] = useState(null);
  const blankEdit = { customer_name: "", customer_phone: "", description: "", address: "", slot: "", duration: "60", price: "", materials: "" };
  const [edit, setEdit] = useState(blankEdit);

  const toLocalInput = (isoStr) => {
    if (!isoStr) return "";
    const d = new Date(isoStr);
    if (isNaN(d)) return String(isoStr).slice(0, 16);
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  };

  const openEdit = (j) => {
    setEdit({
      customer_name: j.customer_name || "", customer_phone: j.customer_phone || "",
      description: j.description || "", address: j.address || "",
      slot: toLocalInput(j.slot_start), duration: String(j.duration_minutes || 60),
      price: j.job_price ?? "", materials: j.material_cost ?? "",
    });
    setEditing(j);
  };

  const saveEdit = async () => {
    if (!edit.customer_name.trim()) { toast.error("Customer name is required"); return; }
    const prevSlot = editing.slot_start;
    const body = {
      customer_name: edit.customer_name.trim(),
      customer_phone: edit.customer_phone.trim() || null,
      description: edit.description.trim() || "Booking",
      address: edit.address.trim() || null,
      duration_minutes: Number(edit.duration),
    };
    if (edit.slot) body.slot_start = new Date(edit.slot).toISOString();
    if (edit.price !== "") body.job_price = Number(edit.price);
    if (edit.materials !== "") body.material_cost = Number(edit.materials);
    const res = await api.patch(`/jobs/${editing.id}`, body);
    toast.success("Booking updated");
    const changedSlot = body.slot_start && body.slot_start !== prevSlot;
    const status = res.data?.status;
    setEditing(null);
    load();
    if (changedSlot && ["approved", "confirmed"].includes(status)) setNotify(res.data);
  };
  const setE = (k) => (e) => setEdit((f) => ({ ...f, [k]: e.target.value }));

  const load = () => api.get("/jobs").then((r) => setJobs(r.data));
  useEffect(() => { load(); }, []);

  const openApprove = (job) => {
    setApproving(job);
    setDuration(String(job.duration_minutes || 60));
    setSlot(job.slot_start ? job.slot_start.slice(0, 16) : "");
  };

  const submitApprove = async () => {
    if (!slot) { toast.error("Pick a date & time"); return; }
    setBusy(true);
    try {
      const res = await api.post(`/jobs/${approving.id}/approve`, { duration_minutes: Number(duration), slot_start: new Date(slot).toISOString() });
      toast.success("Job time set");
      setApproving(null);
      setNotify(res.data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not approve"); }
    finally { setBusy(false); }
  };

  const setStatus = async (job, status) => {
    const res = await api.patch(`/jobs/${job.id}`, { status });
    toast.success(`Booking marked ${status}`);
    if (status === "confirmed") setNotify(res.data || job);
    load();
  };

  const buildMessage = (j) => {
    const biz = company?.name || "us";
    const who = j.customer_name && j.customer_name !== "Unknown" ? j.customer_name : "there";
    const job = j.description ? ` about your ${j.description.toLowerCase()}` : "";
    if (j.slot_start) {
      const when = new Date(j.slot_start).toLocaleString([], { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
      return `Hi ${who}, it's ${biz}${job}. I can come out on ${when} — does that suit you? Just reply to confirm, or let me know a better time and I'll rearrange. Thanks!`;
    }
    return `Hi ${who}, it's ${biz}${job}. Thanks for getting in touch — I'll give you a call shortly to arrange a time. Thanks!`;
  };

  const intlPhone = (phone) => {
    let d = (phone || "").replace(/\D/g, "");
    if (d.startsWith("0")) d = "44" + d.slice(1);
    return d;
  };

  const textCustomer = (j) => {
    if (!j.customer_phone) { toast.error("No phone number for this customer"); return; }
    window.location.href = `sms:${j.customer_phone}?&body=${encodeURIComponent(buildMessage(j))}`;
  };

  const whatsappCustomer = (j) => {
    if (!j.customer_phone) { toast.error("No phone number for this customer"); return; }
    window.open(`https://wa.me/${intlPhone(j.customer_phone)}?text=${encodeURIComponent(buildMessage(j))}`, "_blank");
  };

  const callCustomer = (j) => {
    if (!j.customer_phone) { toast.error("No phone number for this customer"); return; }
    window.location.href = `tel:${j.customer_phone}`;
  };

  const deleteJob = async (job) => {
    if (!window.confirm("Delete this booking? This can't be undone.")) return;
    await api.delete(`/jobs/${job.id}`);
    toast.success("Booking deleted");
    load();
  };

  if (!jobs) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const q = query.trim().toLowerCase();
  const match = (j) => {
    if (!q) return true;
    const hay = [
      j.customer_name, j.description, j.customer_phone, j.address, j.requested_date,
      j.slot_start ? new Date(j.slot_start).toLocaleDateString("en-GB") : "",
      j.slot_start ? new Date(j.slot_start).toLocaleDateString("en-GB", { month: "long" }) : "",
    ].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  };
  const requested = jobs.filter((j) => j.status === "requested" && match(j));
  const active = jobs.filter((j) => ["approved", "confirmed"].includes(j.status) && match(j));
  const completed = jobs.filter((j) => j.status === "completed" && match(j));
  const cancelled = jobs.filter((j) => j.status === "cancelled" && match(j));
  const completedProfit = completed.reduce((s, j) => s + ((j.job_price || 0) - (j.material_cost || 0)), 0);

  const createInvoice = (j) => {
    navigate("/dashboard/invoices", { state: { prefillJob: {
      customer_name: j.customer_name, customer_phone: j.customer_phone, customer_email: j.customer_email, address: j.address,
      description: j.description || j.trade || "",
    } } });
  };

  const CompactRow = ({ j }) => (
    <div data-testid={`booking-card-${j.id}`} className="flex items-center gap-3 py-2.5 px-4 border-b border-white/5 last:border-0 hover:bg-secondary/40 transition-[background-color] duration-150">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{j.customer_name || "Unknown"} <span className="text-muted-foreground font-normal">· {j.description || "Job"}</span></p>
        <p className="text-xs text-muted-foreground">{j.slot_start ? new Date(j.slot_start).toLocaleDateString() : (j.requested_date || "—")}</p>
      </div>
      {j.status === "completed" && (j.job_price != null || j.material_cost != null) && (
        <span className="text-sm font-semibold text-primary shrink-0">£{((j.job_price || 0) - (j.material_cost || 0)).toFixed(0)}</span>
      )}
      {j.status === "completed" && company?.is_pro && (
        <Button data-testid={`invoice-btn-${j.id}`} onClick={() => createInvoice(j)} title="Create invoice" variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-muted-foreground hover:text-primary shrink-0"><FileText className="h-3.5 w-3.5" /></Button>
      )}
      <Button data-testid={`edit-btn-${j.id}`} onClick={() => openEdit(j)} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-muted-foreground hover:text-foreground shrink-0"><Pencil className="h-3.5 w-3.5" /></Button>
      <Button data-testid={`delete-btn-${j.id}`} onClick={() => deleteJob(j)} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-muted-foreground hover:text-red-400 shrink-0"><Trash2 className="h-3.5 w-3.5" /></Button>
    </div>
  );

  const Card = ({ j }) => (
    <div data-testid={`booking-card-${j.id}`} className="bg-[#18181b] border border-white/10 rounded-sm p-5 hover:border-primary/40 transition-[border-color] duration-200">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-wider font-semibold px-2.5 py-1 rounded-sm capitalize" style={{ backgroundColor: `${statusColor[j.status]}22`, color: statusColor[j.status] }}>{j.status}</span>
        <span className="font-display text-lg tracking-wide text-primary">{j.trade || "—"}</span>
      </div>
      <p className="text-sm mb-3 min-h-[40px]">{j.description || "No description"}</p>
      <div className="space-y-1.5 text-sm text-muted-foreground mb-4">
        <p className="flex items-center gap-2"><User className="h-3.5 w-3.5" /> {j.customer_name || "Unknown"} {j.customer_phone ? `· ${j.customer_phone}` : ""}</p>
        <p className="flex items-center gap-2"><CalendarClock className="h-3.5 w-3.5" /> {j.slot_start ? new Date(j.slot_start).toLocaleString() : `Wants a call: ${j.requested_date || "TBC"}`}</p>
        {j.duration_minutes && <p className="flex items-center gap-2"><Clock className="h-3.5 w-3.5" /> {DURATIONS.find((d) => d.v === j.duration_minutes)?.l || `${j.duration_minutes} min`}</p>}
        {j.address && <p className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> {j.address}</p>}
      </div>

      {j.status === "completed" && (j.job_price != null || j.material_cost != null) && (
        <div className="grid grid-cols-3 gap-2 mb-3 text-center" data-testid={`money-${j.id}`}>
          <div className="bg-[#0f0f11] border border-white/10 rounded-sm py-2"><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Charged</p><p className="text-sm font-semibold">£{(j.job_price || 0).toFixed(0)}</p></div>
          <div className="bg-[#0f0f11] border border-white/10 rounded-sm py-2"><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Materials</p><p className="text-sm font-semibold">£{(j.material_cost || 0).toFixed(0)}</p></div>
          <div className="bg-[#0f0f11] border border-primary/40 rounded-sm py-2"><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Profit</p><p className="text-sm font-semibold text-primary">£{((j.job_price || 0) - (j.material_cost || 0)).toFixed(0)}</p></div>
        </div>
      )}

      {j.customer_phone && j.status !== "cancelled" && j.status !== "completed" && (
        <div className="flex gap-2 mb-2">
          <Button data-testid={`text-btn-${j.id}`} onClick={() => textCustomer(j)} variant="outline" size="sm" className="flex-1 rounded-sm border-white/10 gap-1.5 text-xs"><MessageSquare className="h-3.5 w-3.5" /> Text</Button>
          <Button data-testid={`whatsapp-btn-${j.id}`} onClick={() => whatsappCustomer(j)} variant="outline" size="sm" className="flex-1 rounded-sm border-white/10 gap-1.5 text-xs"><MessageCircle className="h-3.5 w-3.5" style={{ color: "#25d366" }} /> WhatsApp</Button>
        </div>
      )}

      {company?.is_pro && (j.status === "approved" || j.status === "confirmed") && (
        <Button data-testid={`card-invoice-btn-${j.id}`} onClick={() => createInvoice(j)} variant="outline" className="w-full rounded-sm border-primary/40 text-primary hover:bg-primary/10 gap-2 mb-2"><FileText className="h-4 w-4" /> Create invoice</Button>
      )}

      {j.status === "requested" && (
        <div className="flex gap-2">
          <Button data-testid={`approve-btn-${j.id}`} onClick={() => openApprove(j)} className="flex-1 rounded-sm bg-primary hover:bg-orange-600 text-white">Review & Approve</Button>
          <Button onClick={() => setStatus(j, "cancelled")} variant="outline" size="icon" className="rounded-sm border-white/10"><XCircle className="h-4 w-4" /></Button>
        </div>
      )}
      {j.status === "approved" && (
        <div className="flex flex-col gap-2">
          <Button data-testid={`confirm-btn-${j.id}`} onClick={() => setStatus(j, "confirmed")} className="rounded-sm bg-green-600 hover:bg-green-700 text-white gap-2"><CheckCircle2 className="h-4 w-4" /> Confirm booking</Button>
          <Button data-testid={`edit-btn-${j.id}`} onClick={() => openEdit(j)} variant="outline" className="rounded-sm border-white/10 gap-2"><Pencil className="h-4 w-4" /> Edit</Button>
          <Button onClick={() => setStatus(j, "cancelled")} variant="outline" className="rounded-sm border-white/10 text-muted-foreground">Cancel</Button>
        </div>
      )}
      {j.status === "confirmed" && (
        <div className="flex flex-col gap-2">
          <Button data-testid={`edit-btn-${j.id}`} onClick={() => openEdit(j)} variant="outline" className="rounded-sm border-white/10 gap-2"><Pencil className="h-4 w-4" /> Edit</Button>
          <Button data-testid={`complete-btn-${j.id}`} onClick={() => openComplete(j)} variant="outline" className="w-full rounded-sm border-white/10">Mark completed</Button>
        </div>
      )}
      {(j.status === "completed" || j.status === "cancelled") && (
        <div className="flex gap-2">
          <Button data-testid={`edit-btn-${j.id}`} onClick={() => openEdit(j)} variant="outline" className="flex-1 rounded-sm border-white/10 gap-2"><Pencil className="h-4 w-4" /> Edit</Button>
          <Button data-testid={`delete-btn-${j.id}`} onClick={() => deleteJob(j)} variant="outline" size="icon" className="rounded-sm border-white/10 text-muted-foreground hover:text-red-400 hover:border-red-400/40"><Trash2 className="h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-8" data-testid="bookings-page">
      <div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight">BOOKINGS</h1>
        <p className="text-muted-foreground mt-1">Review requests, set durations and confirm slots. Deposits are arranged directly by you with the customer.</p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="bookings-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by customer, job or month…" className="rounded-sm bg-[#0f0f11] border-white/10 pl-9" />
        {query && <button data-testid="clear-search" onClick={() => setQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><XCircle className="h-4 w-4" /></button>}
      </div>

      <section>
        <h2 className="font-display text-xl tracking-wide mb-3 flex items-center gap-2">NEW REQUESTS
          {requested.length > 0 && <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-sm">{requested.length}</span>}
        </h2>
        {requested.length === 0 ? <p className="text-sm text-muted-foreground">No new requests. Ava will drop them here as they come in.</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{requested.map((j) => <Card key={j.id} j={j} />)}</div>
        )}
      </section>

      {active.length > 0 && (
        <section>
          <h2 className="font-display text-xl tracking-wide mb-3">SCHEDULED</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{active.map((j) => <Card key={j.id} j={j} />)}</div>
        </section>
      )}

      {completed.length > 0 && (
        <section>
          <button data-testid="toggle-completed" onClick={() => setShowCompleted((v) => !v)} className="flex items-center gap-2 w-full text-left mb-3 group">
            {showCompleted ? <ChevronDown className="h-5 w-5 text-muted-foreground" /> : <ChevronRight className="h-5 w-5 text-muted-foreground" />}
            <h2 className="font-display text-xl tracking-wide">DONE</h2>
            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-sm">{completed.length}</span>
            <span className="ml-auto text-sm text-muted-foreground">£{completedProfit.toFixed(0)} profit</span>
          </button>
          {(showCompleted || q) && (
            <div className="bg-[#18181b] border border-white/10 rounded-sm">{completed.map((j) => <CompactRow key={j.id} j={j} />)}</div>
          )}
        </section>
      )}

      {cancelled.length > 0 && (
        <section>
          <button data-testid="toggle-cancelled" onClick={() => setShowCancelled((v) => !v)} className="flex items-center gap-2 w-full text-left mb-3 group">
            {showCancelled ? <ChevronDown className="h-5 w-5 text-muted-foreground" /> : <ChevronRight className="h-5 w-5 text-muted-foreground" />}
            <h2 className="font-display text-xl tracking-wide">CANCELLED</h2>
            <span className="text-xs bg-white/10 text-muted-foreground px-2 py-0.5 rounded-sm">{cancelled.length}</span>
          </button>
          {(showCancelled || q) && (
            <div className="bg-[#18181b] border border-white/10 rounded-sm">{cancelled.map((j) => <CompactRow key={j.id} j={j} />)}</div>
          )}
        </section>
      )}

      <Dialog open={!!approving} onOpenChange={(o) => !o && setApproving(null)}>
        <DialogContent className="bg-[#0f0f11] border-white/10 text-foreground rounded-sm max-w-md" data-testid="approve-dialog">
          <DialogTitle className="font-display text-2xl tracking-tight font-normal">{approving?.status === "requested" ? "SET JOB TIME" : "CHANGE TIME"}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground -mt-2">{approving?.customer_name} · {approving?.description}</DialogDescription>
          <p className="text-xs text-muted-foreground">Customer wants a call around: <span className="text-foreground">{approving?.requested_date || "—"}</span></p>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Job duration</Label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger data-testid="duration-select" className="rounded-sm bg-[#0f0f11] border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#18181b] border-white/10 text-foreground">
                {DURATIONS.map((d) => <SelectItem key={d.v} value={String(d.v)}>{d.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Job date & time</Label>
            <Input data-testid="slot-input" type="datetime-local" value={slot} onChange={(e) => setSlot(e.target.value)} className="rounded-sm bg-[#0f0f11] border-white/10" />
          </div>
          <Button data-testid="confirm-approve-btn" onClick={submitApprove} disabled={busy} className="w-full rounded-sm bg-primary hover:bg-orange-600 text-white gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (approving?.status === "requested" ? "Save job time" : "Save new time")}
          </Button>
          <p className="text-xs text-muted-foreground text-center -mt-1">You'll be prompted to text, WhatsApp or call the customer with the time straight after.</p>
        </DialogContent>
      </Dialog>

      <Dialog open={!!notify} onOpenChange={(o) => !o && setNotify(null)}>
        <DialogContent className="bg-[#0f0f11] border-white/10 text-foreground rounded-sm max-w-md" data-testid="notify-dialog">
          <DialogTitle className="font-display text-2xl tracking-tight font-normal">TELL YOUR CUSTOMER</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground -mt-2">
            {notify?.slot_start
              ? <>Let <span className="text-foreground">{notify?.customer_name || "them"}</span> know you can do <span className="text-foreground">{new Date(notify.slot_start).toLocaleString([], { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</span> — send a quick message or give them a ring.</>
              : <>Let <span className="text-foreground">{notify?.customer_name || "them"}</span> know — send a quick message or give them a ring.</>}
          </DialogDescription>
          {!notify?.customer_phone && <p className="text-xs text-yellow-400">No phone number was captured for this customer.</p>}
          <div className="flex flex-col gap-2 mt-1">
            <Button data-testid="notify-text-btn" onClick={() => { textCustomer(notify); setNotify(null); }} disabled={!notify?.customer_phone} className="rounded-sm bg-primary hover:bg-orange-600 text-white gap-2 justify-center"><MessageSquare className="h-4 w-4" /> Text customer</Button>
            <Button data-testid="notify-whatsapp-btn" onClick={() => { whatsappCustomer(notify); setNotify(null); }} disabled={!notify?.customer_phone} variant="outline" className="rounded-sm border-white/10 gap-2 justify-center"><MessageCircle className="h-4 w-4" style={{ color: "#25d366" }} /> WhatsApp customer</Button>
            <Button data-testid="notify-call-btn" onClick={() => { callCustomer(notify); }} disabled={!notify?.customer_phone} variant="outline" className="rounded-sm border-white/10 gap-2 justify-center"><Phone className="h-4 w-4" /> Call customer</Button>
            <Button data-testid="notify-later-btn" onClick={() => setNotify(null)} variant="ghost" className="rounded-sm text-muted-foreground">I'll do it later</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!completing} onOpenChange={(o) => !o && setCompleting(null)}>
        <DialogContent className="bg-[#0f0f11] border-white/10 text-foreground rounded-sm max-w-md" data-testid="complete-dialog">
          <DialogTitle className="font-display text-2xl tracking-tight font-normal">MARK COMPLETED</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground -mt-2">{completing?.customer_name} · {completing?.description}. Enter what you charged and your material cost — we'll work out the profit. (Just for you.)</DialogDescription>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Job price (£)</Label>
              <Input data-testid="complete-price" type="number" inputMode="decimal" min="0" value={priceIn} onChange={(e) => setPriceIn(e.target.value)} placeholder="0" className="rounded-sm bg-[#0f0f11] border-white/10" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Material cost (£)</Label>
              <Input data-testid="complete-materials" type="number" inputMode="decimal" min="0" value={matIn} onChange={(e) => setMatIn(e.target.value)} placeholder="0" className="rounded-sm bg-[#0f0f11] border-white/10" />
            </div>
          </div>
          <div className="flex items-center justify-between bg-[#18181b] border border-primary/40 rounded-sm px-4 py-3">
            <span className="text-sm text-muted-foreground">Profit</span>
            <span className="font-display text-2xl tracking-tight text-primary" data-testid="complete-profit">£{(((Number(priceIn) || 0) - (Number(matIn) || 0))).toFixed(0)}</span>
          </div>
          <Button data-testid="confirm-complete-btn" onClick={confirmComplete} className="w-full rounded-sm bg-primary hover:bg-orange-600 text-white">Mark completed</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#0f0f11] border-white/10 text-foreground rounded-sm max-w-md max-h-[88vh] overflow-y-auto" data-testid="edit-dialog">
          <DialogTitle className="font-display text-2xl tracking-tight font-normal">EDIT BOOKING</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground -mt-2">Change any detail — the calendar and your profit totals update automatically.</DialogDescription>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Customer name</Label>
              <Input data-testid="edit-name" value={edit.customer_name} onChange={setE("customer_name")} className="rounded-sm bg-[#0f0f11] border-white/10" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Phone</Label>
              <Input data-testid="edit-phone" value={edit.customer_phone} onChange={setE("customer_phone")} className="rounded-sm bg-[#0f0f11] border-white/10" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Job details</Label>
            <Input data-testid="edit-desc" value={edit.description} onChange={setE("description")} className="rounded-sm bg-[#0f0f11] border-white/10" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Address</Label>
            <Input data-testid="edit-address" value={edit.address} onChange={setE("address")} className="rounded-sm bg-[#0f0f11] border-white/10" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Date & time</Label>
              <Input data-testid="edit-slot" type="datetime-local" value={edit.slot} onChange={setE("slot")} className="rounded-sm bg-[#0f0f11] border-white/10" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Duration</Label>
              <Select value={edit.duration} onValueChange={(v) => setEdit((f) => ({ ...f, duration: v }))}>
                <SelectTrigger data-testid="edit-duration" className="rounded-sm bg-[#0f0f11] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#18181b] border-white/10 text-foreground">
                  {DURATIONS.map((d) => <SelectItem key={d.v} value={String(d.v)}>{d.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Job price (£)</Label>
              <Input data-testid="edit-price" type="number" inputMode="decimal" min="0" value={edit.price} onChange={setE("price")} placeholder="0" className="rounded-sm bg-[#0f0f11] border-white/10" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Material cost (£)</Label>
              <Input data-testid="edit-materials" type="number" inputMode="decimal" min="0" value={edit.materials} onChange={setE("materials")} placeholder="0" className="rounded-sm bg-[#0f0f11] border-white/10" />
            </div>
          </div>
          {(edit.price !== "" || edit.materials !== "") && (
            <div className="flex items-center justify-between bg-[#18181b] border border-primary/40 rounded-sm px-4 py-2.5">
              <span className="text-sm text-muted-foreground">Profit</span>
              <span className="font-display text-xl tracking-tight text-primary" data-testid="edit-profit">£{((Number(edit.price) || 0) - (Number(edit.materials) || 0)).toFixed(0)}</span>
            </div>
          )}
          <Button data-testid="save-edit-btn" onClick={saveEdit} className="w-full rounded-sm bg-primary hover:bg-orange-600 text-white">Save changes</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
